import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  MessageCircle,
  Plus,
  Tag,
  TrendingUp,
  User,
  Users,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import LoadingSpinner from '../LoadingSpinner';
import { TextArea } from '../forms';
import { authService } from '../../services/authService';
import taskService from '../../services/taskService';
import { Task, TaskComment, TaskStatus, TaskProgressUpdate } from '../../types';

interface TaskDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  onTaskUpdated?: () => void;
}

interface CommentFormData {
  content: string;
}

interface ProgressUpdateForm {
  progress: number;
  status: string;
  details: string;
}

type TabType = 'overview' | 'progress' | 'comments';

const TaskDetailsDrawer: React.FC<TaskDetailsDrawerProps> = ({
  isOpen,
  onClose,
  taskId,
  onTaskUpdated,
}) => {
  const [task, setTask] = useState<Task | null>(null);
  const [progressHistory, setProgressHistory] = useState<TaskProgressUpdate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingProgress, setIsUpdatingProgress] = useState(false);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [commentForm, setCommentForm] = useState<CommentFormData>({ content: '' });
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [updateForm, setUpdateForm] = useState<ProgressUpdateForm>({
    progress: 0,
    status: 'on_track',
    details: '',
  });

  // Progress status options
  const progressStatusOptions = [
    { value: 'on_track', label: 'On Track', color: 'text-green-600', bgColor: 'bg-green-100' },
    {
      value: 'need_attention',
      label: 'Need Attention',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
    { value: 'blocked', label: 'Blocked', color: 'text-red-600', bgColor: 'bg-red-100' },
    { value: 'completed', label: 'Completed', color: 'text-blue-600', bgColor: 'bg-blue-100' },
    { value: 'delayed', label: 'Delayed', color: 'text-orange-600', bgColor: 'bg-orange-100' },
  ];

  // Load task details and progress history
  const loadTask = useCallback(async () => {
    if (!taskId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Load task details
      const taskResponse = await taskService.getTask(taskId);
      if (taskResponse.success && taskResponse.data) {
        setTask(taskResponse.data);
        setUpdateForm(prev => ({
          ...prev,
          progress: taskResponse.data?.progress || 0,
        }));
      } else {
        setError(taskResponse.message || 'Failed to load task details');
      }

      // Load progress history
      const historyResponse = await taskService.getTaskProgressHistory(taskId);
      if (historyResponse.success && historyResponse.data) {
        setProgressHistory(historyResponse.data);
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [taskId]);

  // Load task when drawer opens or taskId changes
  useEffect(() => {
    if (isOpen && taskId) {
      loadTask();
    }
  }, [isOpen, taskId, loadTask]);

  // Handle progress update submission
  const handleProgressUpdate = async () => {
    if (!task) return;

    setIsUpdatingProgress(true);
    setError(null);

    try {
      const response = await taskService.updateTaskProgress(task.id, {
        progress: updateForm.progress,
        progressStatus: updateForm.status,
        details: updateForm.details.trim(),
        taskStatus: updateForm.progress === 100 ? 'completed' : task.status,
      });

      if (response.success) {
        setSuccessMessage('Progress updated successfully');
        setUpdateForm(prev => ({ ...prev, details: '' }));
        await loadTask();
        onTaskUpdated?.();
      } else {
        setError(response.message || 'Failed to update progress');
      }
    } catch {
      setError('An unexpected error occurred while updating progress');
    } finally {
      setIsUpdatingProgress(false);
    }
  };

  // Handle mark as complete
  const handleMarkComplete = async () => {
    if (!task) return;

    setIsUpdatingProgress(true);
    setError(null);

    try {
      const response = await taskService.updateTaskProgress(task.id, {
        progress: 100,
        progressStatus: 'completed',
        details: 'Task marked as completed',
        taskStatus: 'completed',
      });

      if (response.success) {
        setSuccessMessage('Task marked as completed');
        await loadTask();
        onTaskUpdated?.();
      } else {
        setError(response.message || 'Failed to complete task');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsUpdatingProgress(false);
    }
  };

  // Handle add comment
  const handleAddComment = async () => {
    if (!task || !commentForm.content.trim()) return;

    setIsAddingComment(true);
    setError(null);

    try {
      const response = await taskService.addComment(task.id, commentForm.content.trim());

      if (response.success) {
        setCommentForm({ content: '' });
        setShowCommentForm(false);
        setSuccessMessage('Comment added successfully');
        // Reload task to get updated comments
        await loadTask();
      } else {
        setError(response.message || 'Failed to add comment');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsAddingComment(false);
    }
  };

  // Format date
  const formatDate = (date: Date | string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  // Get status color
  const getStatusColor = (status: TaskStatus) => {
    const colors = {
      todo: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      review: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      on_hold: 'bg-orange-100 text-orange-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || colors.todo;
  };

  // Get priority color
  const getPriorityColor = (priority: string) => {
    const colors: { [key: string]: string } = {
      low: 'bg-green-500',
      medium: 'bg-yellow-500',
      high: 'bg-orange-500',
      critical: 'bg-red-500',
    };
    return colors[priority] || colors.medium;
  };

  // Get progress status display
  const getProgressStatusDisplay = (status: string) => {
    const option = progressStatusOptions.find(opt => opt.value === status);
    return option || progressStatusOptions[0];
  };

  // Get progress icon
  const getProgressIcon = (status: string) => {
    switch (status) {
      case 'completed': {
        return <CheckCircle className="h-3 w-3" />;
      }
      case 'blocked': {
        return <AlertTriangle className="h-3 w-3" />;
      }
      case 'need_attention':
      case 'delayed': {
        return <Clock className="h-3 w-3" />;
      }
      default: {
        return <TrendingUp className="h-3 w-3" />;
      }
    }
  };

  // Safe user name resolution - Comprehensive function with enhanced debugging
  const getUserDisplayName = (user: any): string => {
    if (!user) {
      return 'Unknown User';
    }

    // Handle string values (sometimes backend sends just a name string)
    if (typeof user === 'string') {
      return user;
    }

    // Try firstName/lastName combination (most common)
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`.trim();
    }

    // Try firstName only
    if (user.firstName) {
      return user.firstName;
    }

    // Try lastName only
    if (user.lastName) {
      return user.lastName;
    }

    // Try nested user object (sometimes data comes as user.user)
    if (user.user) {
      return getUserDisplayName(user.user);
    }

    // Try name field (full name)
    if (user.name) {
      return user.name;
    }

    // Try username field
    if (user.username) {
      return user.username;
    }

    // Try email prefix
    if (user.email) {
      return user.email.split('@')[0];
    }

    // Try _id as last resort (for debugging)
    if (user._id || user.id) {
      return `User ${user._id || user.id}`;
    }

    // Get current user as fallback for new updates
    const currentUser = authService.getStoredUser();
    if (currentUser) {
      if (currentUser.firstName || currentUser.lastName) {
        return `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();
      }
      if (currentUser.name) {
        return currentUser.name;
      }
      if (currentUser.email) {
        return currentUser.email.split('@')[0];
      }
    }

    return 'Current User';
  };

  // Safe display helpers using the comprehensive function
  const getAssignedToName = (assignedTo: any) => {
    if (!assignedTo) return 'Unassigned';
    return getUserDisplayName(assignedTo);
  };

  const getCreatedByName = (createdBy: any) => {
    return getUserDisplayName(createdBy);
  };

  const getCommentAuthorName = (author: any) => {
    return getUserDisplayName(author);
  };

  const getCategoryDisplay = (category: string) => {
    if (!category) return 'General';
    return category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ');
  };

  const getStatusDisplay = (status: TaskStatus) => {
    return status
      .replace('_', ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: FileText },
    { id: 'progress' as TabType, label: 'Progress', icon: TrendingUp },
    { id: 'comments' as TabType, label: 'Comments', icon: MessageCircle },
  ];

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-full max-w-2xl animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden brand-form">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-2">
              <FileText className="h-4 w-4 text-red-500" />
              <h2 className="text-base font-semibold text-gray-900">Task Details</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 bg-gray-50">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-red-600 border-b-2 border-red-600 bg-white'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                  {tab.id === 'comments' && task?.comments?.length && (
                    <span className="ml-1 bg-gray-200 text-gray-600 text-xs rounded-full px-2 py-0.5">
                      {task.comments.length}
                    </span>
                  )}
                  {tab.id === 'progress' && progressHistory.length > 0 && (
                    <span className="ml-1 bg-gray-200 text-gray-600 text-xs rounded-full px-2 py-0.5">
                      {progressHistory.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            ) : task ? (
              <div className="space-y-4">
                {/* Success Message */}
                {successMessage && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-green-800">{successMessage}</p>
                      <button
                        onClick={() => setSuccessMessage(null)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Tab Content */}
                {activeTab === 'overview' && (
                  <div className="space-y-4">
                    {/* Task Header */}
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
                          <p className="mt-1 text-sm text-gray-600">{task.description}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}
                          >
                            {getStatusDisplay(task.status)}
                          </span>
                          <div
                            className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`}
                            title={`${task.priority} priority`}
                          />
                        </div>
                      </div>

                      {/* Current Progress */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-700">
                            Current Progress
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {task.progress || 0}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-red-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${task.progress || 0}%` }}
                          />
                        </div>
                        {task.progressStatus && (
                          <div className="mt-2">
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getProgressStatusDisplay(task.progressStatus).bgColor} ${getProgressStatusDisplay(task.progressStatus).color}`}
                            >
                              {getProgressIcon(task.progressStatus)}
                              <span className="ml-1">
                                {getProgressStatusDisplay(task.progressStatus).label}
                              </span>
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Task Meta */}
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="flex items-center space-x-2">
                          <User className="h-3 w-3 text-gray-400" />
                          <span className="text-gray-600">Assigned to:</span>
                          <span className="font-medium">{getAssignedToName(task.assignedTo)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Users className="h-3 w-3 text-gray-400" />
                          <span className="text-gray-600">Created by:</span>
                          <span className="font-medium">{getCreatedByName(task.createdBy)}</span>
                        </div>
                        {task.dueDate && (
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-3 w-3 text-gray-400" />
                            <span className="text-gray-600">Due:</span>
                            <span className="font-medium">{formatDate(task.dueDate)}</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          <Tag className="h-3 w-3 text-gray-400" />
                          <span className="text-gray-600">Category:</span>
                          <span className="font-medium">{getCategoryDisplay(task.category)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Attachments Section */}
                    {task.attachments && task.attachments.length > 0 && (
                      <div className="border-t border-gray-200 pt-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3">
                          Attachments ({task.attachments.length})
                        </h4>
                        <div className="space-y-2">
                          {task.attachments.map(attachment => (
                            <div
                              key={attachment.id}
                              className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                            >
                              <FileText className="h-4 w-4 text-gray-400" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">
                                  {attachment.fileName}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {(attachment.fileSize / 1024).toFixed(1)} KB
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Progress Tab */}
                {activeTab === 'progress' && (
                  <div className="space-y-4">
                    {/* Completed Task Message */}
                    {task.status === 'completed' && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                          <div>
                            <p className="text-sm text-green-800 font-medium">
                              This task has been completed
                            </p>
                            <p className="text-xs text-green-700 mt-1">
                              You can view the complete progress history below, but cannot add new
                              updates.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Update Form - Only show if task is not completed */}
                    {task.status !== 'completed' && (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <h4 className="text-sm font-medium text-gray-900">Add Progress Update</h4>

                        {/* Progress Slider */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Progress: {updateForm.progress}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={updateForm.progress}
                            onChange={e =>
                              setUpdateForm(prev => ({ ...prev, progress: Number(e.target.value) }))
                            }
                            className="w-full h-2"
                          />
                          <div className="mt-1 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-red-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${updateForm.progress}%` }}
                            />
                          </div>
                        </div>

                        {/* Status Dropdown */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Status
                          </label>
                          <select
                            value={updateForm.status}
                            onChange={e =>
                              setUpdateForm(prev => ({ ...prev, status: e.target.value }))
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          >
                            {progressStatusOptions?.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            )) || <option value="on_track">On Track</option>}
                          </select>
                        </div>

                        {/* Details Text Area */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Progress Details
                          </label>
                          <textarea
                            value={updateForm.details}
                            onChange={e =>
                              setUpdateForm(prev => ({ ...prev, details: e.target.value }))
                            }
                            placeholder="Describe what was accomplished..."
                            rows={3}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          />
                        </div>

                        {/* Form Actions */}
                        <div className="flex items-center justify-end space-x-2 pt-2">
                          <button
                            onClick={handleMarkComplete}
                            disabled={isUpdatingProgress}
                            className="px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                          >
                            {isUpdatingProgress ? 'Updating...' : 'Mark Complete'}
                          </button>
                          <button
                            onClick={handleProgressUpdate}
                            disabled={isUpdatingProgress || !updateForm.details.trim()}
                            className="px-3 py-2 bg-red-500 text-white text-sm font-medium rounded-md hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                          >
                            {isUpdatingProgress ? 'Updating...' : 'Add Update'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Progress History */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-900">
                        Progress History ({progressHistory.length} updates)
                      </h4>

                      {progressHistory.length > 0 ? (
                        <div className="space-y-3">
                          {progressHistory.map((update, index) => (
                            <div
                              key={update.id || index}
                              className="bg-white border border-gray-200 rounded-lg p-3"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <span
                                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getProgressStatusDisplay(update.progressStatus || 'on_track').bgColor} ${getProgressStatusDisplay(update.progressStatus || 'on_track').color}`}
                                    >
                                      {getProgressIcon(update.progressStatus || 'on_track')}
                                      <span className="ml-1">
                                        {
                                          getProgressStatusDisplay(
                                            update.progressStatus || 'on_track'
                                          ).label
                                        }
                                      </span>
                                    </span>
                                    <span className="text-sm font-semibold text-gray-900">
                                      {update.progress}%
                                    </span>
                                  </div>

                                  <p className="text-sm text-gray-700 mb-2">{update.details}</p>

                                  <div className="flex items-center space-x-3 text-xs text-gray-500">
                                    <span>By {getUserDisplayName(update.updatedBy)}</span>
                                    <span>{formatDate(update.createdAt)}</span>
                                  </div>
                                </div>

                                <div className="ml-3">
                                  <div className="w-12 bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-red-500 h-2 rounded-full"
                                      style={{ width: `${update.progress}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <TrendingUp className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">
                            No progress updates yet
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            Start tracking progress by adding your first update.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Comments Tab */}
                {activeTab === 'comments' && (
                  <div className="space-y-4">
                    {/* Add Comment Form */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-900">Add Comment</h4>
                        <button
                          onClick={() => setShowCommentForm(!showCommentForm)}
                          className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {showCommentForm ? 'Cancel' : 'Add Comment'}
                        </button>
                      </div>

                      {showCommentForm && (
                        <div className="space-y-3">
                          <TextArea
                            label="Comment"
                            value={commentForm.content}
                            onChange={value => setCommentForm({ content: value })}
                            placeholder="Add your comment..."
                            rows={3}
                          />
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => {
                                setShowCommentForm(false);
                                setCommentForm({ content: '' });
                              }}
                              className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleAddComment}
                              disabled={isAddingComment || !commentForm.content.trim()}
                              className="px-3 py-2 bg-red-500 text-white text-sm font-medium rounded-md hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                            >
                              {isAddingComment ? 'Adding...' : 'Add Comment'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Comments List */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-900">
                        Comments ({task.comments?.length || 0})
                      </h4>

                      {task.comments && task.comments.length > 0 ? (
                        <div className="space-y-3">
                          {task.comments.map((comment: TaskComment) => (
                            <div
                              key={comment.id}
                              className="bg-white border border-gray-200 rounded-lg p-3"
                            >
                              <div className="flex space-x-3">
                                <div className="flex-shrink-0">
                                  <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                    <User className="h-4 w-4 text-gray-400" />
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="text-sm font-medium text-gray-900">
                                      {getCommentAuthorName(comment.author)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {formatDate(comment.createdAt)}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-700">{comment.content}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">
                            No comments yet
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            Start a conversation by adding the first comment.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p className="text-gray-500">Task not found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default TaskDetailsDrawer;
